<template>
  <div class="ba-sitemap-node-report">
    <header class="report-header">
      <div class="url">
        <a :href="nodeUrl" target="_blank" rel="noopener noreferrer">
        <span class="title">
          <i class="icon icon--ba-link gray"></i>
          {{ node.title || node.name || 'No page Title' }}
        </span>
        </a>
        <span class="link">
          <span
            v-for="(part, idx) in displayPathParts"
            :key="`display-path-${idx}`"
            :class="{ 'path-parameter': part.dynamic }"
          >{{ part.text }}</span>
        </span>
      </div>
      <div class="thumbnail-carousel">
        <div class="thumbnail-container">
          <div class="thumbnail-wrap"
               :style="[{'width': `${100 * carouselLength}%`}, { 'margin-left': `-${100 * carouselPosition}%` }]">
            <div v-for="device in captureList"
                 class="thumbnail"
                 :class="[ device, { 'no-thumbnail': !imagePath[device] }]"
                 :style="{'width': `${100 / carouselLength}%`}"
                 :key="`thumb-${device}`">
              <p class="alert" v-if="!imagePath[device]">
                <i class="icon icon--ring"></i>
                <span v-html="$t('journey.noScreenshot')"></span>
              </p>
              <div class="image">
                <transition name="fade">
                  <div class="loading" v-show="capturing">
                    <span class="loading-circle">
                      <svg x="0px" y="0px" viewBox="0 0 150 150">
                        <circle class="loading-inner" cx="75" cy="75" r="60"/>
                      </svg>
                    </span>
                  </div>
                </transition>
                <div class="capture"
                     :style="{ 'background-image': `url('${getS3Url(imagePath[device] || '')}')` }"></div>
                <div class="meta" v-if="imagePath[device]">
                  <button
                    v-if="!capturing"
                    :class="{ disabled: captureDisabled }"
                    @click="onCapture(device)"
                  >
                    <i class="icon icon--retake-white"></i>
                    {{ $t('common.retake') }}
                  </button>
                </div>
                <div class="buttons" v-else>
                  <button
                    v-if="!capturing"
                    :class="{ disabled: captureDisabled }"
                    @click="onCapture(device)"
                  >
                    <i class="icon icon--retake-white"></i>
                    {{ $t('journey.screenshot') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-if="carouselLength > 1">
          <button class="move left"
                  :disabled="carouselPosition < 1"
                  @click="moveCarouselLeft">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
              <path fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.073 2.5L5 8 10.073 13.527" />
            </svg>
          </button>
          <button class="move right"
                  :disabled="carouselPosition >= carouselLength - 1"
                  @click="moveCarouselRight">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
              <path fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.073 2.5L6 8 11.073 13.527" transform="matrix(-1 0 0 1 17.073 0)"/>
            </svg>
          </button>
        </template>
        <div class="bullets">
          <span v-for="idx in Array.from({ length: carouselLength }, (_, i) => i)"
                class="bullet"
                :class="{ active: carouselPosition === idx }"
                :key="`bullet${idx}`"
                @click="moveCarousel(idx)"></span>
        </div>
      </div>
    </header>
    <div class="report-body">
      <div v-if="hasGroupedUrls" class="notice">
        <h5>{{ $t('common.note') }}</h5>
        <p class="description">{{ $t('observedSitemap.notice.description') }}</p>
        <div class="example" v-html="noticeExample"></div>
      </div>
      <div class="detail">
        <dl class="detail-list">
          <div
            v-for="field in fields"
            :key="field.key"
            class="detail-row"
          >
            <dt>
              <span>{{ field.label }}</span>
              <i
                v-if="field.tooltip"
                class="icon icon--info-outline-white size-14"
                v-btf-tooltip:top="{ contents: field.tooltip, size: 220 }"
              ></i>
            </dt>
            <dd>
              <template v-if="field.parts && field.parts.length">
                <span
                  v-for="(part, idx) in field.parts"
                  :key="`${field.key}-part-${idx}`"
                  :class="{ 'path-parameter': part.dynamic }"
                >{{ part.text }}</span>
              </template>
              <template v-else>{{ field.value == null || field.value === '' ? '-' : field.value }}</template>
            </dd>
          </div>
        </dl>
      </div>
      <div v-if="inspectionInfo.available" class="detail inspection-result">
        <div class="inspection-summary">
          <div class="inspection-card schema-card" :class="{ exists: inspectionInfo.schemaTagExists }">
            <span class="inspection-icon">
              <i class="icon icon--page-tag size-24"></i>
            </span>
            <div class="inspection-text">
              <span class="inspection-label">{{ $t('observedSitemap.inspection.schemaTag') }}</span>
              <strong class="inspection-value">
                {{ inspectionInfo.schemaTagExists
                  ? $t('observedSitemap.inspection.exists')
                  : $t('observedSitemap.inspection.notExists') }}
              </strong>
            </div>
          </div>
          <div
            class="inspection-card geo-card"
            :style="{ '--geo-score-color': getGeoScoreColor() }"
          >
            <div class="geo-score-ring">
              <svg viewBox="0 0 60 32" aria-hidden="true">
                <path
                  class="ring-track"
                  d="M4 30 A26 26 0 0 1 56 30"
                  pathLength="100"
                ></path>
                <path
                  class="ring-progress"
                  d="M4 30 A26 26 0 0 1 56 30"
                  pathLength="100"
                  :stroke-dasharray="`${getNormalizedGeoScore()} 100`"
                ></path>
              </svg>
              <strong>{{ inspectionInfo.geoScore }}</strong>
            </div>
            <div class="inspection-text">
              <span class="inspection-label">{{ $t('observedSitemap.inspection.geoScore') }}</span>
              <strong class="inspection-value">{{ getGeoScoreStatusLabel() }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getS3Url, thousand } from '@/libs/utils'
import { getUrlParameterParts } from '@/libs/sitemap'

function parseImagePath (imagePath) {
  if (!imagePath) return null
  if (typeof imagePath === 'object') return imagePath

  const source = String(imagePath).trim()
  if (!source) return null

  try {
    return JSON.parse(source.replace(/'/g, '"'))
  } catch (e) {
    return source
  }
}

export default {
  name: 'BaSitemapNodeReport',
  props: {
    node: {
      type: Object,
      required: true
    },
    captureProcessing: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      carouselPosition: 0
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'isDemo'
    ]),
    capturing () {
      if (!this.captureProcessing) return false
      return this.captureProcessing.urlId === (this.node.gUrlId || this.node.urlId || this.node.key)
    },
    captureDisabled () {
      return this.isDemo || !(this.node.gUrlId || this.node.urlId || this.node.key)
    },
    nodeUrl () {
      if (this.node.url) return this.node.url
      if (!this.node.origin) return this.node.pathname || this.node.path || ''
      return `${this.node.origin}${this.node.pathname || this.node.path || '/'}`
    },
    displayPath () {
      return this.node.pathname || this.node.path || '/'
    },
    displayPathParts () {
      return getUrlParameterParts(this.displayPath)
    },
    imagePath () {
      const imagePath = parseImagePath(this.node.imagePath)
      if (!imagePath) return {}

      if (typeof imagePath === 'string') {
        return {
          desktop: imagePath
        }
      }

      return ['desktop', 'tablet', 'phone'].reduce((images, device) => {
        if (imagePath[device]) {
          images[device] = imagePath[device]
        }
        return images
      }, {})
    },
    captureList () {
      return this.journeyOptions.device
        ? [this.journeyOptions.device]
        : ['desktop', 'tablet', 'phone']
    },
    carouselLength () {
      return this.captureList.length
    },
    noticeExample () {
      return this.$t('observedSitemap.notice.example').replace(/(\[[^\]]+\])$/, '<mark>$1</mark>')
    },
    hasGroupedUrls () {
      return [this.node.name, this.node.path, this.node.pathname]
        .filter(Boolean)
        .some(value => /(^|\/):[^/]+/.test(String(value)))
    },
    inspectionInfo () {
      const inspection = this.node.schemaInspection
      const hasInspectionContainer = Object.prototype.hasOwnProperty.call(this.node, 'schemaInspection')
      const hasDirectInspectionData = ['exists', 'score']
        .some(key => Object.prototype.hasOwnProperty.call(this.node, key))

      if (hasInspectionContainer && !inspection) {
        return {
          available: false,
          schemaTagExists: false,
          geoScore: null
        }
      }

      const schemaTagExists = inspection && inspection.exists != null
        ? inspection.exists
        : this.node.exists
      const geoScore = inspection && inspection.score != null
        ? inspection.score
        : this.node.score

      if (hasInspectionContainer || hasDirectInspectionData) {
        if (schemaTagExists == null && geoScore == null) {
          return {
            available: false,
            schemaTagExists: false,
            geoScore: null
          }
        }

        return {
          available: true,
          schemaTagExists: Boolean(schemaTagExists),
          geoScore: geoScore != null ? Number(geoScore) : '-'
        }
      }

      return {
        available: false,
        schemaTagExists: false,
        geoScore: null
      }
    },
    fields () {
      const displayUrl = [this.node.domain, this.node.path]
        .filter(value => value != null && value !== '')
        .join('')
      const fields = [
        { key: 'url', label: this.$t('observedSitemap.fields.url'), value: displayUrl, parts: displayUrl ? getUrlParameterParts(displayUrl) : [] },
        { key: 'children', label: this.$t('observedSitemap.fields.children'), tooltip: this.$t('observedSitemap.tooltips.children'), value: this.node.items ? this.node.items.length : 0 },
        { key: 'pv', label: this.$t('observedSitemap.fields.pv'), value: thousand(this.node.pvCount) }
      ]

      if (this.hasGroupedUrls) {
        fields.splice(2, 0, {
          key: 'groupedUrls',
          label: this.$t('observedSitemap.fields.groupedUrls'),
          tooltip: this.$t('observedSitemap.tooltips.groupedUrls'),
          value: this.node.urlCount
        })
      }

      return fields
    }
  },
  watch: {
    node () {
      this.carouselPosition = 0
    }
  },
  methods: {
    getS3Url (value) {
      if (!value) return ''
      if (/^https?:\/\//.test(value)) return value
      return getS3Url(value)
    },
    moveCarouselLeft () {
      this.carouselPosition = Math.max(this.carouselPosition - 1, 0)
    },
    moveCarouselRight () {
      this.carouselPosition = Math.min(this.carouselPosition + 1, this.carouselLength - 1)
    },
    moveCarousel (idx) {
      this.carouselPosition = idx
    },
    getNormalizedGeoScore () {
      const score = Number(this.inspectionInfo.geoScore)
      return Number.isFinite(score)
        ? Math.min(100, Math.max(0, score))
        : 0
    },
    getGeoScoreColor () {
      const score = this.getNormalizedGeoScore()

      if (score <= 20) return '#d94b4b'
      if (score <= 40) return '#df7b22'
      if (score <= 60) return '#c49a00'
      if (score <= 80) return '#9aa210'
      return '#57AB00'
    },
    getGeoScoreStatusLabel () {
      const score = this.getNormalizedGeoScore()

      if (score <= 20) return this.$t('observedSitemap.inspection.geoScoreStatus.risky')
      if (score <= 40) return this.$t('observedSitemap.inspection.geoScoreStatus.insufficient')
      if (score <= 60) return this.$t('observedSitemap.inspection.geoScoreStatus.average')
      if (score <= 80) return this.$t('observedSitemap.inspection.geoScoreStatus.good')
      return this.$t('observedSitemap.inspection.geoScoreStatus.excellent')
    },
    onCapture (device) {
      if (this.captureDisabled) return
      this.$emit('capture', this.node, device)
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-sitemap-node-report {
  box-sizing: border-box;
  width: 100%;
}

.report-header {
  padding: 40px 20px 14px 20px;
  background-color: #4b5263;

  .url {
    position: relative;
    padding-right: 45px;

    .title {
      position: relative;
      display: block;
      font-size: 18px;
      line-height: 23px;
      font-weight: 500;
      color: #ffffff;
      margin-top: 2px;
      padding-left: 24px;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.19);
      transition: padding-right 0.3s $ease-out-quart;

      i {
        position: absolute;
        top: 4px;
        left: 0;
        margin-right: 5px;
        transform: translateY(-1px);
      }
    }

    .link {
      margin-top: 10px;
      padding-left: 24px;
      line-height: 1.38;
      display: block;
      font-size: 13px;
      color: #c1c1c1;
      word-break: break-all;
    }
  }

  .thumbnail-carousel {
    position: relative;
    white-space: nowrap;
    margin-top: 11px;

    .thumbnail-container {
      width: 100%;
      overflow: hidden;

      .thumbnail-wrap {
        transition: margin-left 0.3s cubic-bezier(.28, .12, .42, .94);
      }
    }

    .move {
      position: absolute;
      top: calc(50% - 26px);
      width: 28px;
      height: 28px;
      padding-top: 3px;
      border-radius: 5px;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
      background-color: #fff;
      transition: opacity 0.3s ease;

      svg {
        path {
          stroke: #444444;
          transition: stroke 0.2s ease;
        }
      }

      &:disabled {
        opacity: 0;
        cursor: default;
      }

      &:not(:disabled):hover {
        background-color: #efefef;
      }

      &:not(:disabled):active {
        background-image: linear-gradient(to top, #888888 0%, #666666 100%);
        box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.06);
        color: #ffffff;

        svg {
          path {
            stroke: #ffffff;
          }
        }
      }

      &.left {
        left: -15px;
        padding-right: 2px;
      }

      &.right {
        right: -15px;
        padding-left: 2px;
      }
    }

    .bullets {
      text-align: center;
      padding-top: 18px;

      .bullet {
        display: inline-block;
        width: 8px;
        height: 8px;
        margin: 0 5px;
        background-color: #fff;
        opacity: 0.2;
        transition: opacity 0.2s ease;
        border-radius: 5px;
        cursor: pointer;

        &.active {
          opacity: 1;
        }
      }
    }
  }

  .thumbnail {
    position: relative;
    display: inline-block;

    .alert {
      position: absolute;
      width: calc(100% - 50px);
      padding: 8px 0 8px 30px;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      border-radius: 2px;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 3px 0 rgba(0, 0, 0, 0.12);
      background-color: #f4e439;
      color: #555555;
      font-size: 12px;
      z-index: 3;
      text-align: center;

      .icon {
        position: absolute;
        top: 50%;
        left: 4px;
        transform: translateY(-50%);
      }
    }

    .image {
      box-shadow: 0 12px 13px -7px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.15);
      border: solid 2px #ffffff;
      background-color: #ffffff;
      max-width: 100%;
      border-radius: 10px;
      box-sizing: border-box;
      margin: 0 auto;
      overflow: hidden;
      position: relative;

      .loading {
        position: absolute;
        top: calc(50% - 27px);
        left: calc(50% - 19px);
        padding: 7px;
        border-radius: 8px;
        background-color: #ffffff;
        width: 24px;
        height: 24px;
        z-index: 10;

        .loading-circle {
          position: absolute;
          width: 24px;
          height: 24px;
          transform: translate(-50%, -50%);
          animation: loading 3s linear infinite;
          transition: opacity 0.3s ease;

          svg {
            height: 100%;
          }

          .loading-inner {
            stroke: {
              dashoffset: 0;
              dasharray: 300;
              width: 20;
              miterlimit: 10;
              linecap: round;
            }

            animation: loading-circle 2s linear infinite;
            stroke: $brandColor;
            fill: transparent;
          }
        }
      }

      @keyframes loading {
        0% {
          transform: rotate(0);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes loading-circle {
        0% {
          stroke-dashoffset: 0
        }
        100% {
          stroke-dashoffset: -600;
        }
      }

      .capture {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-size: cover;
        border-radius: 8px;
      }

      button {
        border-radius: 4px;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4);
        background-color: #272727;
        color: #ffffff;
        padding: 5px 7px 4px 7px;
        transition: background-color 0.2s ease;
        white-space: nowrap;

        .icon {
          position: relative;
          top: -1px;
          vertical-align: middle;
        }

        &:hover {
          background-color: #4a4a4a;
        }

        &.disabled {
          opacity: 0.5;
          cursor: default;
        }
      }

      .meta {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 48px;
        box-sizing: border-box;
        background-color: rgba(0, 0, 0, 0.85);
        transform: translateY(100%);
        transition: transform 0.3s ease;

        button {
          position: absolute;
          right: 8px;
          bottom: 9px;
        }
      }

      .buttons {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &:hover .meta {
        transform: translateY(0);
      }
    }

    &.desktop {
      &:after {
        width: 390px;
      }
      .image {
        width: 390px;
        height: 252px;
      }
    }

    &.tablet {
      &:after {
        width: 192px;
      }
      .image {
        width: 192px;
        height: 252px;
      }
    }

    &.phone {
      &:after {
        width: 162px;
      }
      .image {
        width: 162px;
        height: 252px;
      }
    }

    &.no-thumbnail {
      .image {
        .capture {
          transform: scale(1.175);
          filter: blur(15px);
        }
      }

      &.tablet,
      &.phone {
        .alert {
          top: 22px;
          width: 257px;
        }
      }
    }
  }
}

.report-body {
  padding: 0 20px 35px 20px;
  .detail {
    position: relative;
    border-radius: 14px;
    box-shadow: 0 12px 13px -7px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.15);
    background-color: #ffffff;
    margin: 20px 0;
    padding: 20px;
    font-weight: 500;
  }

  .inspection-result {
    margin-top: -4px;
    padding: 12px 18px;
  }

  .inspection-summary {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;

    &::before {
      content: "";
      position: absolute;
      top: 12px;
      bottom: 12px;
      left: 50%;
      width: 1px;
      background-color: #e3e5e8;
      transform: translateX(-50%);
    }
  }

  .inspection-card {
    display: grid;
    grid-template-rows: 42px minmax(38px, auto);
    align-items: center;
    justify-items: center;
    row-gap: 10px;
    min-width: 0;
    min-height: 104px;
    padding: 14px 12px;
    box-sizing: border-box;
  }

  .inspection-text {
    min-width: 0;
    text-align: center;
    align-self: start;
  }

  .inspection-label {
    display: block;
    color: #8a8f98;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }

  .inspection-value {
    display: block;
    margin-top: 4px;
    color: #666666;
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
  }

  .schema-card {
    .inspection-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border: 2px solid #c7ccd1;
      border-radius: 50%;
      background-color: #ffffff;
      box-sizing: border-box;

      .icon--page-tag {
        width: 20px;
        height: 20px;
        filter: grayscale(1);
        opacity: 0.4;
      }
    }

    &.exists {
      .inspection-icon {
        border-color: #0071e3;
      }

      .icon--page-tag {
        filter: none;
        opacity: 1;
      }

      .inspection-value {
        color: #006fdc;
      }
    }
  }

  .geo-card {
    .inspection-value {
      color: var(--geo-score-color, #57AB00);
    }
  }

  .geo-score-ring {
    position: relative;
    width: 60px;
    height: 42px;
    transform: translateY(-6px);
    color: var(--geo-score-color, #57AB00);

    svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    path {
      fill: none;
      stroke-width: 4px;
    }

    .ring-track {
      stroke: #e1e3e0;
    }

    .ring-progress {
      stroke: var(--geo-score-color, #57AB00);
      stroke-linecap: round;
    }

    > strong {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      color: currentColor;
      font-size: 18px;
      line-height: 20px;
      font-weight: 700;
      text-align: center;
    }
  }
}

.detail-list {
  display: grid;
  grid-template-columns: 100px minmax(0, 1fr);
  gap: 10px 14px;
  margin: 0;

  .detail-row {
    display: contents;
  }

  dt {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #999;
    font-size: 14px;
    font-weight: 500;

    .icon {
      flex: 0 0 auto;
      cursor: help;
    }
  }

  dd {
    margin: 0;
    color: #767676;
    font-size: 14px;
    line-height: 1.45;
    overflow-wrap: anywhere;
    word-break: break-all;
  }
}

.path-parameter {
  padding: 1px 3px;
  border-radius: 3px;
  background-color: rgba(189, 189, 189, 0.24);
}

.notice {
  margin-top: 18px;
  border-radius: 10px;
  background-color: #f4f4f4;
  position: relative;
  padding: 1px 0;
  text-align: center;

  h5 {
    position: absolute;
    top: 12px;
    left: 12px;
    border-radius: 3px;
    background-color: #1199af;
    font-weight: normal;
    color: #ffffff;
    padding: 3px 4px;
    font-size: 11px;
    text-transform: uppercase;
  }

  .description {
    margin: 36px 15px 15px 15px;
    font-size: 13px;
    line-height: 1.5;
    color: #a47e40;
  }

  .example {
    margin: 15px 15px 18px 15px;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 500;
    color: #666666;
    white-space: pre-line;

    ::v-deep mark {
      padding: 2px 0;
      background-color: rgba(252, 0, 78, 0.23);
    }
  }
}
</style>
